import { Search, X } from 'lucide-react';
import type * as React from 'react';
import { cn } from './cn';
import { Input } from './Input';

/**
 * A text field with a search icon, and room for it.
 *
 * Both applications had one. The product's placed the magnifier over an input
 * whose text also starts at `px-3` until the padding was fixed here; the
 * console's copy was made from the version before that and never got the clear
 * button, the `pointer-events-none` that stops the icon swallowing a click, or
 * the `aria-hidden` that keeps it out of the accessible name. Two copies of a
 * search box is how one of them ends up worse.
 *
 * The labels are props, not translations: this package cannot reach either
 * app's i18n, and the console has none. Each app wraps this with its own words.
 */

export interface SearchFieldProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'type'> {
  value: string;
  onValueChange: (value: string) => void;
  /** Announced on the clear button. Required when `clearable`. */
  clearLabel?: string;
  /** Hides the clear button for fields where an empty value is not meaningful. */
  clearable?: boolean;
  containerClassName?: string;
}

export function SearchField({
  value,
  onValueChange,
  clearLabel,
  clearable = true,
  className,
  containerClassName,
  ...props
}: SearchFieldProps) {
  const showClear = clearable && value.length > 0;

  return (
    <div className={cn('relative', containerClassName)}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        type="search"
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        // Room for the icon on the left, and for the clear button on the right
        // so a long query does not run underneath it.
        className={cn('pl-9', showClear && 'pr-9', className)}
        {...props}
      />
      {showClear ? (
        <button
          type="button"
          onClick={() => onValueChange('')}
          aria-label={clearLabel ?? 'Clear search'}
          className={cn(
            'absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center',
            'rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
            'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring'
          )}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}
